// components/Form2307Form.jsx
import React, { useState, useMemo } from 'react';
import formAPI from '../api/formAPI';
import useAtcCodes from '../hooks/useAtcCodes';
import { isCorporateFiler } from '../utils/sawtFormType';
import ChartOfAccountsSearch from './ChartOfAccountsSearch';
import VendorSearch from './VendorSearch';
import MoneyInput from './MoneyInput';
import notify from '../lib/notify';
import { useVendorTerms } from '../utils/terminology';

const MONTH_NAMES = [
    '', 'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];



const getFYKey = (clientId) => (clientId ? `activeFiscalYear:${clientId}` : null);

const resolveFiscalYear = (clientId, selectedYear) => {
    const fromSelected = parseInt(selectedYear, 10);
    if (fromSelected && !isNaN(fromSelected)) return fromSelected;
    const key = getFYKey(clientId);
    const headerFY = key ? parseInt(localStorage.getItem(key), 10) : null;
    return headerFY && !isNaN(headerFY) ? headerFY : null;
};

const newRow = () => ({
    // vendor
    vendorId: null,
    vendorObj: null,
    vendorTinManual: '',
    // certificate
    certificateNumber: '',
    certificateDate: new Date().toISOString().split('T')[0],
    // amounts
    baseAmount: '',
    withholdingRate: '5.00',
    taxWithheld: '',
    // other
    description: '',
    chartOfAccount: null,
    chartOfAccountName: '',
    atcCode: '',
    transactionDate: new Date().toISOString().split('T')[0],
    invoiceNumber: '',
    status: 'received',
});

const recalc = (row) => {
    const base = parseFloat(row.baseAmount) || 0;
    const rate = parseFloat(row.withholdingRate) || 0;
    return { ...row, taxWithheld: ((base * rate) / 100).toFixed(2) };
};

// `correctionReason` is required by the backend: this form is the correction /
// reissue path now that certificates are generated automatically at posting.
const Form2307Form = ({ client, onRecordAdded, selectedYear, correctionReason = '' }) => {
  const term = useVendorTerms();
    const [rows, setRows] = useState([newRow()]);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    // ATC codes come from the BIR reference table, not a hardcoded list, so a
    // BIR circular is a data change rather than a redeploy.
    // On a 2307 the CLIENT is the payee — each row selects the Payor (the
    // withholding agent). So the ATC class follows the client's entity type:
    // an individual taxpayer is never offered a WC code.
    const payeeClass = isCorporateFiler(client?.clientType) ? 'corporation' : 'individual';
    const { options: atcOptions, byCode: atcByCode } = useAtcCodes('2307', { payeeClass });

    const fiscalYearNum = useMemo(
        () => resolveFiscalYear(client?._id, selectedYear),
        [client?._id, selectedYear]
    );

    // ── Row helpers ────────────────────────────────────────────────────────
    const updateRow = (index, patch, skipRecalc = false) => {
        setRows((prev) => {
            const next = [...prev];
            const updated = { ...next[index], ...patch };
            next[index] = skipRecalc ? updated : recalc(updated);
            return next;
        });
    };

    const addRow = () => {
        if (rows.length < 5) setRows((prev) => [...prev, newRow()]);
    };

    const removeRow = (index) => {
        if (rows.length > 1) setRows((prev) => prev.filter((_, i) => i !== index));
    };

    const handleVendorSelect = (index, vendor) => {
        if (!vendor) {
            updateRow(index, {
                vendorId: null, vendorObj: null, vendorTinManual: '',
            });
        } else {
            updateRow(index, {
                vendorId: vendor._id,
                vendorObj: vendor,
                vendorTinManual: '',
                atcCode: vendor.defaultAtcCode2307 || rows[index].atcCode || '',
                withholdingRate: vendor.defaultWithholdingRate2307
                    ? String(vendor.defaultWithholdingRate2307)
                    : rows[index].withholdingRate,
            });
        }
    };

    const handleCOASelect = (index, account) => {
        if (!account) {
            updateRow(index, { chartOfAccount: null, chartOfAccountName: '' });
        } else {
            updateRow(index, {
                chartOfAccount: account._id,
                chartOfAccountName: `${account.accountNumber} - ${account.accountName}`,
                description: rows[index].description || account.accountName,
            });
        }
    };

    // ── Submit ────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!fiscalYearNum) {
            setError('Please select a fiscal year.');
            return;
        }

        const valid = rows.filter(
            (r) => r.vendorId && parseFloat(r.baseAmount) > 0
        );

        if (valid.length === 0) {
            setError(`Please add at least one record with ${term.lower} and base amount.`);
            return;
        }

        // Per-row vendor check
        const missingVendor = rows.some(
            (r) => parseFloat(r.baseAmount) > 0 ? !r.vendorId : false
        );
        if (missingVendor) {
            setError(`Please select a ${term.lower} (withholding agent) for every row with data.`);
            return;
        }

        const missingRate = rows.some(
            (r) => parseFloat(r.baseAmount) > 0 && r.vendorId && !(parseFloat(r.withholdingRate) > 0)
        );
        if (missingRate) {
            setError('Withholding Rate must be greater than 0 for all records.');
            return;
        }

        const missingDescription = rows.some(
            (r) => parseFloat(r.baseAmount) > 0 && !r.description?.trim()
        );
        if (missingDescription) {
            setError('Description is required for every row with data.');
            return;
        }

        try {
            setSubmitting(true);

            const payload = valid.map((r) => ({
                vendor: r.vendorId,
                description: r.description.trim(),
                baseAmount: parseFloat(r.baseAmount) || 0,
                withholdingRate: parseFloat(r.withholdingRate) || 0,
                taxWithheld: parseFloat(r.taxWithheld) || 0,
                certificateNumber: r.certificateNumber || `2307-${Date.now()}`,
                certificateDate: r.certificateDate || new Date().toISOString(),
                atcCode: r.atcCode || '',
                transactionDate: r.transactionDate || new Date().toISOString(),
                invoiceNumber: r.invoiceNumber || '',
                status: r.status || 'received',
                chartOfAccount: r.chartOfAccount || undefined,
                chartOfAccountName: r.chartOfAccountName || '',
            }));

            const response = await formAPI.createForm2307Records(client._id, payload, correctionReason);
            const savedRecords = response?.data || [];

            setRows([newRow()]);
            setError('');

            // Pass the saved records up — a list filtered by the currently
            // viewed quarter/month needs to know each record's actual
            // (transactionDate-derived) period to show what was just saved.
            if (onRecordAdded) onRecordAdded(savedRecords);

            // The backend derives each record's actual month/fiscalYear from
            // its own transactionDate — build the confirmation message from
            // what was actually saved. A batch can span more than one period
            // if rows have different transaction dates.
            const periodLabels = new Set(
                savedRecords
                    .filter((r) => r?.fiscalYear && r?.month)
                    .map((r) => `${MONTH_NAMES[r.month]} ${r.fiscalYear}`)
            );
            const periodText = periodLabels.size === 1
                ? ` for ${Array.from(periodLabels)[0]}`
                : periodLabels.size > 1
                    ? ` across ${periodLabels.size} different periods`
                    : '';
            notify.success('Saved', `${valid.length} Form 2307 record${valid.length !== 1 ? 's' : ''}${periodText}.`);
        } catch (err) {
            console.error('Form 2307 submit error:', err);
            setError('Failed to save: ' + (err?.message || err));
        } finally {
            setSubmitting(false);
        }
    };

    const totals = rows.reduce(
        (acc, r) => ({
            base: acc.base + (parseFloat(r.baseAmount) || 0),
            withheld: acc.withheld + (parseFloat(r.taxWithheld) || 0),
        }),
        { base: 0, withheld: 0 }
    );

    return (
        <div className="p-6">
            <div className="mb-5">
                <h3 className="text-lg font-medium text-ink">Create Form 2307 records</h3>
                <p className="text-sm text-ink-body mt-0.5">
                    Certificate of Creditable Tax Withheld at Source — BIR Form 2307
                </p>
                <p className="text-xs text-ink-body mt-1">
                    FY {fiscalYearNum || '?'} • {client?.name}
                </p>
                <p className="text-xs text-ink-body mt-1">
                    The Transaction Date on each row determines its filing month — no separate month selection needed.
                </p>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded text-sm text-danger-700">
                    {error}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="space-y-5">
                    {rows.map((row, index) => (
                        <div key={index} className="bg-white border border-ink-line rounded-lg p-4">
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-xs font-medium text-ink-body uppercase tracking-wide">
                                    Record {index + 1}
                                </span>
                                {rows.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={() => removeRow(index)}
                                        className="text-xs text-danger-500 hover:text-danger-700"
                                    >
                                        Remove
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Vendor (Withholding Agent) */}
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                         Payor *
                                    </label>
                                    <VendorSearch
                                        value={row.vendorObj}
                                        onSelect={(vendor) => handleVendorSelect(index, vendor)}
                                        placeholder={`Search ${term.lower} / withholding agent...`}
                                    />
                                    {row.vendorId && !row.vendorObj?.tin && (
                                        <input
                                            type="text"
                                            placeholder={`Enter TIN manually (${term.lower} has none on file)`}
                                            value={row.vendorTinManual}
                                            onChange={(e) => updateRow(index, { vendorTinManual: e.target.value })}
                                            className="mt-1 w-full border border-ink-line rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        />
                                    )}
                                </div>

                                {/* Description / COA */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Description <span className="text-danger-500">*</span>
                                    </label>
                                    <ChartOfAccountsSearch
                                        value={row.chartOfAccountName || row.description}
                                        onChange={(v) => updateRow(index, { description: v })}
                                        onSelect={(acc) => handleCOASelect(index, acc)}
                                        placeholder="Description or search chart of accounts"
                                        showLinkedBadge={false}
                                    />
                                    {parseFloat(row.baseAmount) > 0 && !row.description?.trim() && (
                                        <p className="mt-1 text-xs text-danger-500">Description is required.</p>
                                    )}
                                </div>

                                {/* ATC Code */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        ATC Code
                                    </label>
                                    <select
                                        value={row.atcCode}
                                        onChange={(e) => {
                                            const atcCode = e.target.value;
                                            const known = atcByCode[atcCode];
                                            // Populate the rate from the ATC table rather than
                                            // leaving it hand-keyed. Unknown code -> leave the
                                            // existing rate alone; never guess one.
                                            updateRow(index, known
                                                ? { atcCode, withholdingRate: String(known.ratePercent) }
                                                : { atcCode });
                                        }}
                                        className="w-full border border-ink-line rounded px-3 py-2 text-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    >
                                        {atcOptions.map((o) => (
                                            <option key={o.value} value={o.value} title={o.description}>{o.label}</option>
                                        ))}
                                    </select>
                                </div>

                                {/* Certificate Number */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Certificate No.
                                    </label>
                                    <input
                                        type="text"
                                        value={row.certificateNumber}
                                        onChange={(e) => updateRow(index, { certificateNumber: e.target.value })}
                                        className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        placeholder="2307-XXXXXX"
                                    />
                                </div>

                                {/* Certificate Date */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Certificate Date
                                    </label>
                                    <input
                                        type="date"
                                        value={row.certificateDate}
                                        onChange={(e) => updateRow(index, { certificateDate: e.target.value })}
                                        className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    />
                                </div>

                                {/* Withholding Rate */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Withholding Rate (%) <span className="text-danger-500">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        value={row.withholdingRate}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "" || (parseFloat(val) >= 0 && parseFloat(val) <= 100)) {
                                                updateRow(index, { withholdingRate: val });
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const val = parseFloat(e.target.value);
                                            if (isNaN(val) || val < 0) updateRow(index, { withholdingRate: "0" });
                                            else if (val > 100) updateRow(index, { withholdingRate: "100" });
                                        }}
                                        className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        placeholder="e.g. 2"
                                        min="0"
                                        max="100"
                                        step="0.01"
                                        required
                                    />
                                    {/* Warn, never block: a historical certificate legitimately
                                        carries the rate that was in force when it was issued. */}
                                    {row.atcCode
                                        && atcByCode[row.atcCode]
                                        && row.withholdingRate !== ''
                                        && Math.round(parseFloat(row.withholdingRate) * 100)
                                            !== Math.round(atcByCode[row.atcCode].ratePercent * 100) && (
                                        <p className="mt-1 text-xs text-warning-600">
                                            {row.atcCode} is {atcByCode[row.atcCode].ratePercent}% per {atcByCode[row.atcCode].issuance}
                                        </p>
                                    )}
                                </div>

                                {/* Base Amount */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Base Amount (₱) *
                                    </label>
                                    <div className="relative">
                                        <span className="absolute inset-y-0 left-3 flex items-center text-ink-body text-sm pointer-events-none">₱</span>
                                        <MoneyInput
                                            value={row.baseAmount}
                                            onValueChange={(value) => updateRow(index, { baseAmount: value })}
                                            className="pl-7 w-full"
                                        />
                                    </div>
                                </div>

                                {/* Tax Withheld */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Tax Withheld (₱)
                                    </label>
                                    <div className="relative">
                                        <span className="absolute inset-y-0 left-3 flex items-center text-ink-body text-sm pointer-events-none">₱</span>
                                        <MoneyInput
                                            value={row.taxWithheld}
                                            onValueChange={(value) => updateRow(index, { taxWithheld: value }, true)}
                                            className="pl-7 w-full"
                                        />
                                    </div>
                                </div>

                                {/* Transaction Date */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Transaction Date
                                    </label>
                                    {(() => {
                                        const txYear = row.transactionDate ? new Date(row.transactionDate).getFullYear() : null;
                                        const yearMismatch = txYear && fiscalYearNum && txYear !== fiscalYearNum;
                                        return (
                                            <>
                                                <input
                                                    type="date"
                                                    value={row.transactionDate}
                                                    onChange={(e) => updateRow(index, { transactionDate: e.target.value })}
                                                    className={`w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 ${
                                                        yearMismatch ? 'border-danger-400 focus:ring-danger-500' : 'border-ink-line focus:ring-primary-500'
                                                    }`}
                                                />
                                                {yearMismatch && (
                                                    <p className="text-xs text-danger-500 mt-1">
                                                        Transaction Date year ({txYear}) does not match the selected Fiscal Year ({fiscalYearNum}). Please correct one or the other.
                                                    </p>
                                                )}
                                            </>
                                        );
                                    })()}
                                </div>

                                {/* Invoice Number */}
                                <div>
                                    <label className="block text-xs font-medium text-primary-700 mb-1">
                                        Invoice No. (optional)
                                    </label>
                                    <input
                                        type="text"
                                        value={row.invoiceNumber}
                                        onChange={(e) => updateRow(index, { invoiceNumber: e.target.value })}
                                        className="w-full border border-ink-line rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                                        placeholder="INV-XXXXXX"
                                    />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Add row / totals / submit */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                    {rows.length < 5 && (
                        <button
                            type="button"
                            onClick={addRow}
                            className="text-sm text-primary-500 hover:text-primary-700 font-medium"
                        >
                            + Add Another Record
                        </button>
                    )}

                    <div className="ml-auto flex items-center gap-6 text-sm text-ink-body">
                        <span>
                            Total Base: <strong>₱{totals.base.toFixed(2)}</strong>
                        </span>
                        <span>
                            Total EWT: <strong className="text-primary-700">₱{totals.withheld.toFixed(2)}</strong>
                        </span>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-5 py-2 bg-primary-500 text-white text-sm rounded hover:bg-primary-600 disabled:opacity-50"
                        >
                            {submitting ? 'Saving…' : `Save ${rows.filter(r => r.vendorId && parseFloat(r.baseAmount) > 0).length || ''} Record(s)`}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default Form2307Form;








